import moment from 'moment-timezone-custom';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import React from 'react';
import { BiRightArrowAlt } from 'react-icons/bi';
import { useDispatch } from 'react-redux';
import { useAccount } from 'wagmi';
import Divider from '@/components/UI/Divider';
import Pill from '@/components/UI/Pill';
import Button, { ButtonSize, ButtonType } from '@/components/UI/buttons/Button';

import type { ToastModel } from '@/components/UI/toast/Toast';
import { ToastType } from '@/components/UI/toast/Toast';
import Avatar, { AvatarSize } from '@/components/cards/Avatar';
import CollectionInfoCard from '@/components/cards/CollectionInfoCard';
import { BudgetPerAsset } from '@/components/display/BudgetPerAsset';
import { TitleWithChildren } from '@/components/formatting/TitleWithChildren';
import { DATE_FORMATS } from '@/constants/constants';
import useDelegatedNfts from '@/hooks/useDelegatedNfts';
import useNftsOwned from '@/hooks/useNftsOwned';
import { usePageEntrance } from '@/hooks/usePageEntrance';
import { addToast } from '@/store/toastSlice';
import type {
  CollectionFragment,
  DealFragment,
} from '@/utilities/__generated__/graphql';
import { BucketType, createBucketURL } from '@/utilities/createBucketUrl';
import {
  DateThemeMap,
  getHumanizedDate,
  getLocalDate,
} from '@/utilities/dateFormatter';
import { cleanAddress } from '@/utilities/functions';
import { sanitizeAndReplaceMarkup } from '@/utilities/sanitization';

export const DealComponent = ({
  deal,
  requestedNumberOfNfts,
  isCreatingDeal = false,
  hasCollectionLinks = true,
}: {
  deal: DealFragment;
  requestedNumberOfNfts: string | number;
  isCreatingDeal?: boolean;
  hasCollectionLinks?: boolean;
}) => {
  usePageEntrance();
  const { address } = useAccount();
  const { status } = useSession();
  const router = useRouter();
  const { nfts } = useNftsOwned(address as string);
  const { delegatedNfts } = useDelegatedNfts(address as string);
  const dispatch = useDispatch();

  const ownedCollections = [...nfts, ...delegatedNfts].map((collection) => {
    return cleanAddress(collection.address);
  });
  const collectionDeals = deal.CollectionDeals.filter(
    ({ Collection }) => Collection?.nft_contract_address
  );

  const ownedCollectionsInDeal = collectionDeals.filter(
    ({ Collection }: { Collection: CollectionFragment }) => {
      return ownedCollections.includes(
        cleanAddress(Collection.nft_contract_address)
      );
    }
  );

  const isBiddingSubmissionExpired = moment(deal.bidding_expiration).isBefore(
    moment()
  );
  const canMakeProposal =
    ownedCollectionsInDeal.length > 0 && !isBiddingSubmissionExpired;
  const isDealCreator =
    deal.wallet_address === address && status === 'authenticated';

  const localLicenseExpirationDate = getLocalDate(
    deal.license_expiration,
    DATE_FORMATS.MMMDYhm
  );
  const localSubmissionExpirationDate = getLocalDate(
    deal.bidding_expiration,
    DATE_FORMATS.MMMDYhm
  );
  const [humanizedLocalSubmissionExpirationDate, dateEnum] = getHumanizedDate(
    localSubmissionExpirationDate
  );
  const { theme, pulse, dot } = DateThemeMap[dateEnum];
  return (
    <div className="w-full">
      <div className="container mx-auto">
        <div className="flex flex-col justify-start md:space-y-3">
          <p className="enter-el-inherit text-neutral-50">
            {!!deal.id && (isDealCreator || !canMakeProposal)
              ? 'View Proposals'
              : isCreatingDeal
              ? 'Publish Opportunity'
              : 'Submit Proposal'}
          </p>
          <h1 className="enter-el-inherit pb-5 font-gt-haptik text-heading-4-mobile font-bold text-neutral-100 md:text-heading-4">
            Review Opportunity
          </h1>
          <div className="enter-el-inherit flex flex-col items-start justify-between space-y-3 md:flex-row md:items-center md:space-y-0">
            <div className="flex flex-col items-start space-y-4">
              <div className="flex items-center space-x-4 md:flex-row">
                <Avatar
                  size={AvatarSize.md}
                  link={`/brand/${encodeURI(deal.Brand.username ?? '')}`}
                  src={createBucketURL(
                    BucketType.Brand,
                    deal.Brand.username,
                    deal.Brand.updated_at
                  )}
                />
                <div className="flex flex-col">
                  <p className="text-lg text-neutral-100">{deal.title}</p>
                  <p className="text-sm text-neutral-50">
                    {deal.Brand.company_name}
                    {deal.Brand.legal_name ? ` / ${deal.Brand.legal_name}` : ''}
                  </p>
                </div>
              </div>
            </div>
            {!!deal.id && (
              <div className="flex w-full items-center justify-start md:ml-auto md:w-auto md:justify-start">
                <Button
                  size={ButtonSize.primary}
                  type={
                    !canMakeProposal && !isDealCreator
                      ? ButtonType.disabled
                      : ButtonType.primary
                  }
                  onClick={() => {
                    if (canMakeProposal || isDealCreator) {
                      void router.push({
                        pathname: `/deal/${deal.id}/${
                          isDealCreator ? 'bids' : 'submitBid'
                        }/`,
                      });
                    } else {
                      dispatch(
                        addToast({
                          title:
                            'No eligible collections found. Please connect with a valid wallet.',
                          type: ToastType.warning,
                        } as ToastModel)
                      );
                    }
                  }}
                >
                  <p className="flex items-center justify-center">
                    {isDealCreator ? 'View Proposals' : 'Submit Proposal'}
                    <span className="pl-2">
                      <BiRightArrowAlt />
                    </span>
                  </p>
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="enter-el-inherit w-full py-6">
          <div className="pb-6 text-neutral-50">Eligible Collections</div>
          <div className="grid gap-x-2 gap-y-6 md:grid-cols-2 md:space-y-0 lg:grid-cols-3">
            {collectionDeals.map(
              ({
                Collection,
                count,
              }: {
                Collection: CollectionFragment;
                count: number;
              }) => {
                return (
                  <div key={Collection.name}>
                    {hasCollectionLinks ? (
                      <Link
                        href={`/collection/${cleanAddress(
                          Collection.nft_contract_address
                        )}`}
                        passHref
                      >
                        <div className="col-span-1 cursor-pointer">
                          <CollectionInfoCard
                            collection={Collection}
                            count={count}
                          />
                        </div>
                      </Link>
                    ) : (
                      <CollectionInfoCard
                        collection={Collection}
                        count={count}
                      />
                    )}
                  </div>
                );
              }
            )}
          </div>
        </div>
        <Divider />
        <div
          className={`enter-el-inherit grid w-full grid-cols-1 gap-x-2 gap-y-6 py-6 md:grid-cols-2 ${
            isCreatingDeal ? '' : 'lg:grid-cols-3'
          }`}
        >
          <TitleWithChildren title="Submission Deadline">
            <Pill
              theme={theme}
              classes="my-0"
              pulse={pulse}
              showDot={dot}
              title={localSubmissionExpirationDate}
            >
              {humanizedLocalSubmissionExpirationDate}
            </Pill>
          </TitleWithChildren>

          {!isCreatingDeal && (
            <TitleWithChildren title="Number of Assets Submitted">
              {String(deal.Bids_aggregate.aggregate?.count ?? 0)}
            </TitleWithChildren>
          )}
          {/* //TODO do we want to show the dates in this countdown format or a date and what format of date? */}
          <TitleWithChildren title="License Expiration">
            {localLicenseExpirationDate}
          </TitleWithChildren>
          <BudgetPerAsset
            paymentCurrency={deal.payment_currency}
            minBudget={deal.min_budget}
            maxBudget={deal.max_budget}
            royaltyPercentage={deal.royalty_percentage}
            isCreatingDeal={isCreatingDeal}
            collectionAddress={
              collectionDeals[0]?.Collection.nft_contract_address
            }
            publishedDate={deal.created_at}
            bonusConfig={deal.bonus_config ?? undefined}
          />
          <TitleWithChildren title="Requested Number of Assets">
            {requestedNumberOfNfts as string}
          </TitleWithChildren>
        </div>
        <Divider />
        <div className="enter-el-inherit flex w-full flex-col pt-6 text-left">
          <p className="pb-2 text-left text-neutral-50">Summary</p>
          <p className="hidden font-satoshi-italic" />
          <p
            className="justify-self-end pb-2 font-regular text-black sm:pb-0"
            dangerouslySetInnerHTML={sanitizeAndReplaceMarkup(
              deal.description ?? '',
              /\n/g,
              '<br>'
            )}
          />
        </div>
      </div>
    </div>
  );
};
